import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { Save, RotateCcw, Upload, Download, Settings, Zap, Shield, Wifi } from 'lucide-react';
import { api } from '../../hooks/useApi';

export function ConfigPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await api.getConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to load config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateConfig(config);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save config:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (section, key, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const handleExport = async () => {
    try {
      const data = await api.exportConfig();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ems-config.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const tabs = [
    { id: 'general', label: 'Üldine', icon: Settings },
    { id: 'optimizer', label: 'Optimeerimine', icon: Zap },
    { id: 'safety', label: 'Ohutus', icon: Shield },
    { id: 'connections', label: 'Ühendused', icon: Wifi },
  ];

  if (loading) {
    return (
      <div className="card p-8 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
        <p className="mt-4 text-gray-400">Laadin seadistusi...</p>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="card-header flex items-center justify-between">
        <h3 className="font-semibold text-gray-200">Seadistused</h3>
        <div className="flex gap-2">
          <button onClick={handleExport} className="btn btn-secondary text-sm">
            <Download className="w-4 h-4 mr-1" />
            Ekspordi
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="btn btn-primary text-sm"
          >
            {saving ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                Salvesta
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <div className="flex">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
                'border-b-2 -mb-px',
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'general' && config?.general && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Töörežiim
              </label>
              <select
                value={config.general.mode}
                onChange={(e) => handleChange('general', 'mode', e.target.value)}
                className="input w-full"
              >
                <option value="price">Hinna optimeerimine</option>
                <option value="peak_shaving">Tipukoormuse piiramine</option>
                <option value="self_consumption">Oma tarbimine</option>
                <option value="manual">Käsijuhtimine</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Süsteemi nimi
              </label>
              <input
                type="text"
                value={config.general.systemName || ''}
                onChange={(e) => handleChange('general', 'systemName', e.target.value)}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ajavöönd
              </label>
              <select
                value={config.general.timezone}
                onChange={(e) => handleChange('general', 'timezone', e.target.value)}
                className="input w-full"
              >
                <option value="Europe/Tallinn">Europe/Tallinn</option>
                <option value="Europe/Helsinki">Europe/Helsinki</option>
                <option value="Europe/Riga">Europe/Riga</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'optimizer' && config?.optimizer && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  SOC min (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={config.optimizer.socMin}
                  onChange={(e) => handleChange('optimizer', 'socMin', parseFloat(e.target.value))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  SOC max (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={config.optimizer.socMax}
                  onChange={(e) => handleChange('optimizer', 'socMax', parseFloat(e.target.value))}
                  className="input w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Laadimise hinnapiir (EUR/MWh)
                </label>
                <input
                  type="number"
                  value={config.optimizer.chargeThreshold}
                  onChange={(e) => handleChange('optimizer', 'chargeThreshold', parseFloat(e.target.value))}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Laadi kui hind alla selle</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tühjendamise hinnapiir (EUR/MWh)
                </label>
                <input
                  type="number"
                  value={config.optimizer.dischargeThreshold}
                  onChange={(e) => handleChange('optimizer', 'dischargeThreshold', parseFloat(e.target.value))}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Tühjenda kui hind üle selle</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max laadimise võimsus (kW)
                </label>
                <input
                  type="number"
                  value={config.optimizer.maxChargePower}
                  onChange={(e) => handleChange('optimizer', 'maxChargePower', parseFloat(e.target.value))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max tühjendamise võimsus (kW)
                </label>
                <input
                  type="number"
                  value={config.optimizer.maxDischargePower}
                  onChange={(e) => handleChange('optimizer', 'maxDischargePower', parseFloat(e.target.value))}
                  className="input w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tipukoormuse piir (kW)
              </label>
              <input
                type="number"
                value={config.optimizer.peakLimit}
                onChange={(e) => handleChange('optimizer', 'peakLimit', parseFloat(e.target.value))}
                className="input w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Peak shaving režiimis</p>
            </div>
          </div>
        )}

        {activeTab === 'safety' && config?.safety && (
          <div className="space-y-6">
            <div className="p-4 bg-yellow-900/20 border border-yellow-700 rounded-lg">
              <p className="text-sm text-yellow-400">
                Need seadistused mõjutavad süsteemi ohutuspiiranguid. Muuda ettevaatlikult!
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Elemendi max pinge (mV)
                </label>
                <input
                  type="number"
                  value={config.safety.vcellMax}
                  onChange={(e) => handleChange('safety', 'vcellMax', parseFloat(e.target.value))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Elemendi min pinge (mV)
                </label>
                <input
                  type="number"
                  value={config.safety.vcellMin}
                  onChange={(e) => handleChange('safety', 'vcellMin', parseFloat(e.target.value))}
                  className="input w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max temperatuur (°C)
                </label>
                <input
                  type="number"
                  value={config.safety.tempMax}
                  onChange={(e) => handleChange('safety', 'tempMax', parseFloat(e.target.value))}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Min temperatuur (°C)
                </label>
                <input
                  type="number"
                  value={config.safety.tempMin}
                  onChange={(e) => handleChange('safety', 'tempMin', parseFloat(e.target.value))}
                  className="input w-full"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'connections' && config?.connections && (
          <div className="space-y-6">
            <div className="p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
              <p className="text-sm text-blue-400">
                Ühenduste seadistuste muutmine nõuab süsteemi taaskäivitust.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-200 mb-4">BMS (Lithtech)</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">IP aadress</label>
                  <input
                    type="text"
                    value={config.connections.bms?.host || ''}
                    onChange={(e) => handleChange('connections', 'bms', { ...config.connections.bms, host: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Port</label>
                  <input
                    type="number"
                    value={config.connections.bms?.port || 502}
                    onChange={(e) => handleChange('connections', 'bms', { ...config.connections.bms, port: parseInt(e.target.value) })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Slave ID</label>
                  <input
                    type="number"
                    value={config.connections.bms?.slaveId || 1}
                    onChange={(e) => handleChange('connections', 'bms', { ...config.connections.bms, slaveId: parseInt(e.target.value) })}
                    className="input w-full"
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-200 mb-4">PCS (IN-POWER)</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">IP aadress</label>
                  <input
                    type="text"
                    value={config.connections.pcs?.host || ''}
                    onChange={(e) => handleChange('connections', 'pcs', { ...config.connections.pcs, host: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Port</label>
                  <input
                    type="number"
                    value={config.connections.pcs?.port || 502}
                    onChange={(e) => handleChange('connections', 'pcs', { ...config.connections.pcs, port: parseInt(e.target.value) })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Slave ID</label>
                  <input
                    type="number"
                    value={config.connections.pcs?.slaveId || 1}
                    onChange={(e) => handleChange('connections', 'pcs', { ...config.connections.pcs, slaveId: parseInt(e.target.value) })}
                    className="input w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfigPanel;
